import type { ClientExceptionEntity } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useScrollToTop } from '@react-navigation/native';
import type {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
  QueryObserverResult,
  RefetchOptions
} from '@tanstack/react-query';
import { LinearGradient } from 'expo-linear-gradient';
import type { FC, ReactElement } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import type { ListRenderItemInfo } from 'react-native';
import { FlatList, StyleSheet, View } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';

import { FeedStateView } from '@/features/posts/components/feed/FeedStateView';
import {
  GroupPostScreenFeedCard,
  PostScreenFeedCard
} from '@/features/posts/components/feed/PostScreenCards';
import { SIZE } from '@/features/posts/constants/params';
import { useTrackFeedPostViews } from '@/features/posts/hooks/useTrackFeedPostViews';
import type { GenericPostDetail } from '@/features/posts/types/posts';
import { ErrorStateView } from '@/shared/components/state-view/ErrorStateView';
import { Refresher } from '@/shared/components/state-view/Refresher';
import { hasItems } from '@/shared/utils/array';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

const makeStyles = () =>
  StyleSheet.create({
    container: { flex: 1 },
    feed: { marginHorizontal: 12, marginTop: 12 },
    errorOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1,
      elevation: 1,
      backgroundColor: 'transparent'
    }
  });

type SimplePostListProps = {
  disableGradient?: boolean;
  posts?: GenericPostDetail[];
  isLoading: boolean;
  isError?: boolean;
  fetchNextPage: (
    options?: FetchNextPageOptions
  ) => Promise<
    InfiniteQueryObserverResult<GenericPostDetail[], ClientExceptionEntity>
  >;
  hasNextPage: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  refetch: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<GenericPostDetail[], ClientExceptionEntity>>;
  isRefetching: boolean;
  header?: ReactElement;
  hideEndView?: boolean;
  hideEmptyView?: boolean;
  groupId?: string;
  hidePostType?: boolean;
  canCreatePost?: boolean;
};

export const SimplePostList: FC<SimplePostListProps> = ({
  disableGradient = false,
  posts,
  isLoading,
  isError = false,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  refetch,
  isRefetching,
  header,
  hideEndView = false,
  hideEmptyView = false,
  groupId,
  hidePostType,
  canCreatePost = false
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(), []);

  const renderPost = useCallback(
    (post: ListRenderItemInfo<GenericPostDetail>, hidePostType = false) => {
      if (groupId) {
        return (
          <GroupPostScreenFeedCard
            post={post.item}
            hidePostType={hidePostType}
          />
        );
      }

      return (
        <PostScreenFeedCard post={post.item} hidePostType={hidePostType} />
      );
    },
    [groupId, hidePostType]
  );

  const viewabilityConfigCallbackPairs =
    useTrackFeedPostViews<ListRenderItemInfo<GenericPostDetail>['item']>();

  const listRef = useRef(null);
  useScrollToTop(listRef);

  // TODO: if initialNumToRender < SIZE it seems to break infinite scrolling on some screen sizes (only initialNumToRender items are shown and no second page is loaded)
  return (
    <LinearGradient
      style={styles.container}
      colors={
        disableGradient
          ? ['transparent', 'transparent']
          : [
              theme.colors.surfaceContainer,
              theme.colors.secondaryContainer,
              theme.colors.secondaryContainer
            ]
      }
    >
      {isError && !isLoading && (
        <ErrorStateView
          onRetry={() => void refetch()}
          style={[
            styles.errorOverlay,
            { backgroundColor: theme.colors.surface }
          ]}
        />
      )}
      <FlatList<GenericPostDetail>
        ref={listRef}
        data={posts}
        refreshing={isRefetching}
        onRefresh={refetch}
        refreshControl={<Refresher refreshing={isRefetching} />}
        renderItem={renderPost}
        keyExtractor={(item, idx) => item.postId ?? idx.toString()}
        ListHeaderComponent={header}
        ListEmptyComponent={
          <View>
            {isLoading && <ActivityIndicator size={80} />}
            {!hasItems(posts) &&
              !hasNextPage &&
              !isLoading &&
              !isError &&
              !hideEmptyView && (
                <FeedStateView
                  state={canCreatePost ? 'empty_action' : 'empty'}
                />
              )}
          </View>
        }
        ListFooterComponent={
          <View>
            {isFetchingNextPage && <ActivityIndicator size={80} />}
            {!hasNextPage && !isLoading && !hideEndView && hasItems(posts) && (
              <FeedStateView />
            )}
          </View>
        }
        extraData={[
          isLoading,
          isFetchingNextPage,
          hasNextPage,
          hideEndView,
          hideEmptyView,
          canCreatePost
        ]}
        onEndReached={() =>
          hasNextPage && !isFetchingNextPage && fetchNextPage()
        }
        onEndReachedThreshold={0.8}
        initialNumToRender={SIZE}
        contentContainerStyle={styles.feed}
        showsVerticalScrollIndicator={false}
        removeClippedSubviews={false}
        keyboardShouldPersistTaps="handled"
        viewabilityConfigCallbackPairs={viewabilityConfigCallbackPairs}
      />
    </LinearGradient>
  );
};
